import { useState } from 'react';
import { Button } from '@carbon/react';
import {
  Add,
  ArrowLeft,
  ArrowRight,
  OpenPanelLeft,
  SidePanelClose,
} from '@carbon/react/icons';
import { useAtom } from 'jotai';
import type { DocumentResponse } from 'types/api-responses';
import type { CostOverviewWell } from 'pages/afe-catalog/structuring/types-cost';
import { StructureCosts } from 'pages/afe-catalog/structuring/structure-costs';
import { documentLayoutAtom } from './atoms/document-layout';
import { AddPartyPanel } from './add-party-panel';
import { PartyDetail } from './party-detail';
import type { StructuredPartyListItem, WellDrillIn } from './types';
import style from './structuring-module.module.scss';

/**
 * Toggles the structuring page's left panel.
 *
 * @return {JSX.Element} The icon button.
 */
export function LeftPanelToggle() {
  const [layout, setLayout] = useAtom(documentLayoutAtom);
  return (
    <Button
      kind="tertiary"
      size="md"
      hasIconOnly
      renderIcon={layout.showLeftPanel ? SidePanelClose : OpenPanelLeft}
      iconDescription={
        layout.showLeftPanel ? 'Hide Left Panel' : 'Show Left Panel'
      }
      tooltipPosition="bottom"
      tooltipAlignment="end"
      onClick={() =>
        setLayout({ ...layout, showLeftPanel: !layout.showLeftPanel })
      }
    />
  );
}

interface NavigatorProps {
  noun: string;
  position: number;
  count: number;
  atFirst: boolean;
  atLast: boolean;
  onPrev: () => void;
  onNext: () => void;
}

/**
 * Prev/next pager showing "{noun} {position} of {count}".
 *
 * @param {NavigatorProps} props - The component props.
 * @return {JSX.Element} The navigator.
 */
function Navigator({
  noun,
  position,
  count,
  atFirst,
  atLast,
  onPrev,
  onNext,
}: NavigatorProps) {
  return (
    <div className={style.partyNavigator}>
      <Button
        kind="ghost"
        size="md"
        hasIconOnly
        renderIcon={ArrowLeft}
        iconDescription={`Previous ${noun}`}
        tooltipPosition="bottom"
        tooltipAlignment="center"
        disabled={atFirst}
        onClick={onPrev}
      />
      <span className={style.partyNavigatorLabel}>
        {noun} {position} of {count}
      </span>
      <Button
        kind="ghost"
        size="md"
        hasIconOnly
        renderIcon={ArrowRight}
        iconDescription={`Next ${noun}`}
        tooltipPosition="bottom"
        tooltipAlignment="center"
        disabled={atLast}
        onClick={onNext}
      />
    </div>
  );
}

interface WellDrillInViewProps {
  drillIn: WellDrillIn;
  title?: string;
}

/**
 * A well's Match Review, with a prev/next navigator over the wells; AFE, DO and
 * RC wells and RC line items share it.
 *
 * @param {WellDrillInViewProps} props - The drill-in and the section title.
 * @return {JSX.Element} The well drill-in.
 */
export function WellDrillInView({
  drillIn,
  title = 'Structure Wells',
}: WellDrillInViewProps) {
  return (
    <div className={style.sectionWrapper}>
      <div className={style.sectionHeader}>
        <div className={style.sectionTitleSlot}>
          <h2>{title}</h2>
        </div>
        <div className={style.sectionNav}>
          <Navigator
            noun="Well"
            position={drillIn.index + 1}
            count={drillIn.count}
            atFirst={drillIn.atFirst}
            atLast={drillIn.atLast}
            onPrev={drillIn.onPrev}
            onNext={drillIn.onNext}
          />
          <Button kind="tertiary" size="md" onClick={drillIn.onBack}>
            Back to Overview
          </Button>
        </div>
      </div>
      {drillIn.renderMatch()}
    </div>
  );
}

interface PartyDrillInProps {
  document: DocumentResponse;
  parties: StructuredPartyListItem[];
  index: number;
  onSelectIndex: (index: number) => void;
  onMutated: () => void;
}

/**
 * One party's Match Review, with a prev/next navigator over the parties.
 *
 * @param {PartyDrillInProps} props - The component props.
 * @return {JSX.Element} The party drill-in.
 */
export function PartyDrillIn({
  document,
  parties,
  index,
  onSelectIndex,
  onMutated,
}: PartyDrillInProps) {
  const [addPartyOpen, setAddPartyOpen] = useState(false);
  return (
    <div className={style.sectionWrapper}>
      <div className={style.sectionHeader}>
        <div className={style.sectionTitleSlot}>
          <h2>Structure Parties</h2>
        </div>
        <div className={style.sectionNav}>
          <Navigator
            noun="Party"
            position={index + 1}
            count={parties.length}
            atFirst={index === 0}
            atLast={index === parties.length - 1}
            onPrev={() => onSelectIndex(Math.max(0, index - 1))}
            onNext={() =>
              onSelectIndex(Math.min(parties.length - 1, index + 1))
            }
          />
          <LeftPanelToggle />
          <Button
            kind="tertiary"
            size="md"
            renderIcon={Add}
            onClick={() => setAddPartyOpen(true)}
          >
            Add Party
          </Button>
        </div>
      </div>
      <PartyDetail
        document={document}
        party={parties[index]}
        index={index}
        onMutated={onMutated}
      />
      <AddPartyPanel
        open={addPartyOpen}
        document={document}
        onClose={() => setAddPartyOpen(false)}
        onAdded={() => {
          setAddPartyOpen(false);
          onMutated();
        }}
      />
    </div>
  );
}

interface AfeCostDrillInProps {
  documentId: number;
  costWells: CostOverviewWell[];
  // An afeHeaderId: cost lives on the header's active scenario.
  selectedCostWellId: number;
  // null returns to the overview.
  onSelect: (afeHeaderId: number | null) => void;
  onMutated: () => void;
}

/**
 * AFE Structure Costs for one well. Same header shape as the well drill-in, but
 * paged over the cost overview's wells, so it stays AFE-specific.
 *
 * @param {AfeCostDrillInProps} props - The component props.
 * @return {JSX.Element} The cost drill-in.
 */
export function AfeCostDrillIn({
  documentId,
  costWells,
  selectedCostWellId,
  onSelect,
  onMutated,
}: AfeCostDrillInProps) {
  const costIndex = costWells.findIndex(
    (w) => w.afeHeaderId === selectedCostWellId
  );
  const currentCostWell = costIndex >= 0 ? costWells[costIndex] : null;
  return (
    <div
      id="afe-cost-detail"
      className={style.sectionWrapper}
      // Deep-link scroll target: clears the pinned header using the live
      // measured offset (usePinnedHeaderOffset).
      style={{ scrollMarginTop: 'var(--structuring-scroll-offset, 232px)' }}
    >
      <div className={style.sectionHeader}>
        <div className={style.sectionTitleSlot}>
          <h2>Structure Costs</h2>
        </div>
        <div className={style.sectionNav}>
          <Navigator
            noun="Well"
            position={(costIndex < 0 ? 0 : costIndex) + 1}
            count={costWells.length}
            atFirst={costIndex <= 0}
            atLast={costIndex < 0 || costIndex === costWells.length - 1}
            onPrev={() => {
              if (costIndex > 0) onSelect(costWells[costIndex - 1].afeHeaderId);
            }}
            onNext={() => {
              if (costIndex >= 0 && costIndex < costWells.length - 1) {
                onSelect(costWells[costIndex + 1].afeHeaderId);
              }
            }}
          />
          <Button kind="tertiary" size="md" onClick={() => onSelect(null)}>
            Back to Overview
          </Button>
        </div>
      </div>
      <StructureCosts
        documentId={documentId}
        wellId={selectedCostWellId}
        wellName={currentCostWell?.wellName ?? currentCostWell?.uwi ?? null}
        wellNo={costIndex >= 0 ? costIndex + 1 : undefined}
        onMutated={onMutated}
      />
    </div>
  );
}
